import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Sparkles, Upload, X, File } from 'lucide-react';
import { toast } from 'sonner';
import { useParserSandbox } from '@/hooks/useParserSandbox';
import { ParserSupplierSelect } from '@/components/parser/ParserSupplierSelect';
import { CanonicalExtractedDataDisplay } from '@/components/parser/CanonicalExtractedDataDisplay';
import { transformParsed, type BillHeader, type LineItem } from '@/utils/parserTransform';
import { downloadBillCSV } from '@/utils/csvExport';
import { ParseProgress } from '@/components/parser/ParseProgress';
import { SimplePDFPreview } from '@/components/parser/SimplePDFPreview';
import { EnhancedPDFPreview } from '@/components/parser/EnhancedPDFPreview';

export default function ParserSandbox() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<any>(null);
  const [parserConfig] = useState({
    parserType: 'ai' as 'ai' | 'rule' | 'hybrid',
    ocrEngine: 'tesseract' as 'tesseract' | 'google-vision',
    confidenceThreshold: 0.5,
    customRules: {}
  });
  
  const [parsedData, setParsedData] = useState<any>(null);
  const [canonicalData, setCanonicalData] = useState<{ header: BillHeader; lineItems: LineItem[] } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [parseProgress, setParseProgress] = useState({ percent: 0, stage: '' });
  const [useObjectTag, setUseObjectTag] = useState(true);
  const [useEnhancedPreview, setUseEnhancedPreview] = useState(true);
  const [userDefinedFields, setUserDefinedFields] = useState<any[]>([]);

  const { testParse, unparsedBills, templates, suppliers } = useParserSandbox();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('Please upload a PDF file');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file?.type === 'application/pdf') {
      setSelectedFile(file);
    } else {
      toast.error('Please upload a PDF file');
    }
  };

  const handleParseData = async () => {
    if (!selectedFile) {
      toast.error('Please upload a file');
      return;
    }

    if (!selectedSupplier) {
      toast.error('Please select a supplier');
      return;
    }

    setIsLoading(true);
    setParseProgress({ percent: 10, stage: 'connecting' });
    
    try {
      // Staged progress simulation
      setTimeout(() => setParseProgress({ percent: 20, stage: 'authenticating' }), 300);
      setTimeout(() => setParseProgress({ percent: 40, stage: 'processing' }), 600);
      setTimeout(() => setParseProgress({ percent: 60, stage: 'extracting' }), 1200);
      setTimeout(() => setParseProgress({ percent: 90, stage: 'analyzing' }), 2000);

      const result = await testParse.mutateAsync({
        file: selectedFile,
        supplierId: selectedSupplier.id,
        templateId: '1',
        parserType: parserConfig.parserType,
        ocrEngine: parserConfig.ocrEngine,
        confidenceThreshold: parserConfig.confidenceThreshold,
        customRules: parserConfig.customRules
      });

      // Transform to canonical format
      const transformed = transformParsed(
        result,
        selectedSupplier.name,
        parserConfig.confidenceThreshold
      );
      
      setParseProgress({ percent: 100, stage: 'complete' });
      setParsedData(result);
      setCanonicalData(transformed);
      
      toast.success('Parsing complete!');
    } catch (error: any) {
      toast.error(error.message || 'Parsing failed');
    } finally {
      setTimeout(() => {
        setIsLoading(false);
        setParseProgress({ percent: 0, stage: '' });
      }, 500);
    }
  };

  const handleCancelParse = () => {
    setIsLoading(false);
    setParseProgress({ percent: 0, stage: '' });
    toast.info('Parsing cancelled');
  };

  const handleClear = () => {
    setSelectedFile(null);
    setParsedData(null);
    setCanonicalData(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="container mx-auto px-4 py-6 max-w-[1800px]">
        <div className="mb-6 text-center">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
            Parser Sandbox
          </h1>
          <p className="text-slate-600">
            Test and validate AI-powered bill parsing in real-time
          </p>
        </div>

        <Alert className="mb-4 border-blue-200 bg-blue-50">
          <Sparkles className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-sm text-slate-700">
            Upload a bill document to test AI parsing. No data is saved to the database.
          </AlertDescription>
        </Alert>

        {/* Compact Configuration Card */}
        <Card className="shadow-lg border-0 bg-gradient-to-br from-white to-blue-50/30 mb-4">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
              {/* Supplier Selection */}
              <div className="md:col-span-3 space-y-1">
                <Label className="text-xs">Supplier *</Label>
                <ParserSupplierSelect
                  value={selectedSupplier?.name || ''}
                  onSelect={(name) => setSelectedSupplier({ id: name, name })}
                />
              </div>

              {/* Parser Type */}
              <div className="md:col-span-2 space-y-1">
                <Label className="text-xs">Parser Type</Label>
                <div className="h-9 flex items-center">
                  <Badge variant={parserConfig.parserType === 'ai' ? 'default' : 'secondary'} className="text-xs">
                    {parserConfig.parserType === 'ai' 
                      ? (selectedSupplier?.name ? `Bot Agent - ${selectedSupplier.name}` : 'Bot Agent')
                      : 'Template'
                    }
                  </Badge>
                </div>
              </div>

              {/* File Upload */}
              <div className="md:col-span-4 space-y-1">
                <Label className="text-xs">Upload Bill *</Label>
                {selectedFile ? (
                  <div className="h-9 flex items-center justify-between gap-2 px-3 border rounded-md bg-green-50 border-green-300">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <File className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span className="text-sm font-medium truncate">{selectedFile.name}</span>
                      <span className="text-xs text-slate-500 flex-shrink-0">
                        {(selectedFile.size / 1024).toFixed(0)} KB
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedFile(null)}
                      className="h-6 w-6 p-0 flex-shrink-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <label className="h-9 flex items-center justify-center gap-2 px-3 border-2 border-dashed rounded-md cursor-pointer hover:border-blue-400 transition-colors">
                    <Upload className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-slate-600">Drop PDF or click</span>
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              {/* Action Buttons */}
              <div className="md:col-span-3 flex gap-2">
                <Button
                  onClick={handleParseData}
                  disabled={!selectedFile || !selectedSupplier || isLoading}
                  className="flex-1 h-9"
                  size="sm"
                >
                  Parse Data
                </Button>
                <Button
                  onClick={handleClear}
                  variant="outline"
                  disabled={!selectedFile && !parsedData}
                  className="h-9"
                  size="sm"
                >
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Two-Pane Layout: PDF Preview + Extracted Data */}
        <div className="ps-shell">
          <div className="ps-grid">
            {/* Left Pane: PDF Preview */}
            <section className="ps-pane ps-pdf">
              <div className="ps-pane-header">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-slate-700">PDF Preview</h3>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setUseEnhancedPreview(!useEnhancedPreview)}
                      className="h-7 px-2 text-xs"
                    >
                      {useEnhancedPreview ? 'Use Simple' : 'Use Enhanced'}
                    </Button>
                    {!useEnhancedPreview && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setUseObjectTag(!useObjectTag)}
                        className="h-7 px-2 text-xs"
                      >
                        {useObjectTag ? 'Use Iframe' : 'Use Object'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <div className="ps-pane-body">
                {selectedFile ? (
                  useEnhancedPreview ? (
                    <EnhancedPDFPreview 
                      file={selectedFile}
                      className="w-full h-full"
                    />
                  ) : (
                    <SimplePDFPreview 
                      file={selectedFile}
                      className="w-full h-full"
                      useObject={useObjectTag}
                    />
                  )
                ) : (
                  <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                    <Upload className="h-16 w-16 text-slate-300 mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No File Selected</h3>
                    <p className="text-sm text-slate-600">
                      Upload a PDF file to preview it here
                    </p>
                  </div>
                )}
              </div>
            </section>

            {/* Right Pane: Extracted Data */}
            <section className="ps-pane ps-extracted">
              <div className="ps-pane-header">
                {canonicalData ? (
                  <CanonicalExtractedDataDisplay 
                    header={canonicalData.header}
                    lineItems={canonicalData.lineItems}
                    rawData={parsedData}
                    onExportCSV={async () => {
                      try {
                        await downloadBillCSV(
                          canonicalData.header,
                          canonicalData.lineItems,
                          parsedData,
                          `parsed_${selectedSupplier?.name || 'bill'}`,
                          userDefinedFields
                        );
                        toast.success('CSV exported successfully');
                      } catch (error) {
                        toast.error('CSV export failed');
                      }
                    }}
                    onFieldUpdate={(field, value) => {
                      setCanonicalData(prev => {
                        if (!prev) return prev;
                        return {
                          ...prev,
                          header: { ...prev.header, [field]: value }
                        };
                      });
                      toast.success('Field updated');
                    }}
                    onAddUserDefinedField={(field) => {
                      const newField = {
                        ...field,
                        id: `udf_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                        created_at: new Date().toISOString()
                      };
                      setUserDefinedFields(prev => [...prev, newField]);
                    }}
                    onUpdateUserDefinedField={(id, field) => {
                      setUserDefinedFields(prev => 
                        prev.map(f => f.id === id ? { ...f, ...field } : f)
                      );
                    }}
                    onDeleteUserDefinedField={(id) => {
                      setUserDefinedFields(prev => prev.filter(f => f.id !== id));
                    }}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                    <div className="text-6xl mb-4">📊</div>
                    <h3 className="text-lg font-semibold mb-2">No Data Yet</h3>
                    <p className="text-sm text-slate-600">
                      Upload a file and parse it to see extracted data
                    </p>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
      
      {isLoading && (
        <ParseProgress
          percent={parseProgress.percent}
          stage={parseProgress.stage}
          onCancel={handleCancelParse}
        />
      )}
    </div>
  );
}
